import classNames from 'classnames';
import { max, min, scaleBand, scaleLinear } from 'd3';
import {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import {
  BAR_END_HOVER_SLOP,
  NEW_THEME_BAR_GAP,
  NEW_THEME_BAR_THICKNESS,
  ReplotChartNames,
} from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  findNearestBandIndex,
  getDimmedPlotColor,
  getFormatter,
  getMaxTextWidth,
  getPrimaryColor,
  getPrimaryColorDark,
  useChartTheme,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import {
  getCursorPosition,
  PlotTooltip,
  type TooltipPosition,
  useTooltipController,
} from '../plot-tooltip';
import styles from './bar-chart.module.css';
import { BarLabel } from './bar-label';

const BAR_LEFT_PADDING = 4;

const MAX_BAR_LABEL_WIDTH_RATIO = 0.9;
const MIN_BAR_LABEL_SIZE = 30;
const BAR_LABEL_PADDING = 4;
const MIN_BAR_WIDTH = 1;

const DIMENSIONS_STANDARD = {
  fontSize: 11,
  paddingOuter: 0.4,
  paddingInner: 0.4,
};

const DIMENSIONS_SMALL = {
  fontSize: 10,
  paddingOuter: 0.4,
  paddingInner: 0.4,
};

export type BarData<TData extends object = object> = {
  label: string;
  value: number | null;
  color?: string;
  id?: string | null;
} & TData;

type BarChartSort = 'ASC' | 'DESC';

type AxisOptions = {
  min?: number;
  max?: number;
};

export type BarChartProps<TData extends object = object> = {
  className?: string;
  data: BarData<TData>[];
  showBarLabels?: boolean;
  sort?: BarChartSort | null;
  xAxis?: AxisOptions;
  /**
   * Ceiling on bar thickness. Height beyond what the rows need is absorbed as
   * outer padding rather than fattening the bars, so a chart in an oversized
   * container keeps the theme's bar weight. Raise it to let the bars grow into
   * the space instead.
   */
  maxBarThickness?: number;
  /**
   * Gap between bars when they are at `maxBarThickness`; below the cap the two
   * scale together, so this really sets the bar-to-gap ratio. The theme default
   * is gappier than the bars (10:12) — lower it for a denser chart.
   */
  barGap?: number;
  tooltipContent?: (data: BarData<TData>) => React.ReactNode;
} & CommonPlotProps;

export const BarChart = <TData extends object = object>({
  className,
  data = [],
  showBarLabels = true,
  sort = null,
  format = FormatType.SI,
  currencyCode = 'USD',
  loading = false,
  xAxis,
  maxBarThickness = NEW_THEME_BAR_THICKNESS,
  barGap = NEW_THEME_BAR_GAP,
  tooltipContent,
}: BarChartProps<TData>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { theme } = useChartTheme();
  const isDarkTheme = theme === 'dark';

  const noDataPatternHref = isDarkTheme
    ? 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPScjNEE1NTY4Jy8+CiAgPHBhdGggZD0nTS0xLDEgbDIsLTIKICAgICAgICAgICBNMCwxMCBsMTAsLTEwCiAgICAgICAgICAgTTksMTEgbDIsLTInIHN0cm9rZT0nIzFhMjAyYycgc3Ryb2tlLXdpZHRoPSczJy8+Cjwvc3ZnPg=='
    : 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPScjQ0JENUUwJy8+CiAgPHBhdGggZD0nTS0xLDEgbDIsLTIKICAgICAgICAgICBNMCwxMCBsMTAsLTEwCiAgICAgICAgICAgTTksMTEgbDIsLTInIHN0cm9rZT0nI2ZmZicgc3Ryb2tlLXdpZHRoPSczJy8+Cjwvc3ZnPg==';

  const defaultBarColor = isDarkTheme
    ? getPrimaryColorDark()
    : getPrimaryColor();

  const { width, height } = useResizeObserver(containerRef);
  const displayData = useMemo(() => {
    if (!sort) return data;

    return [...data].sort((a, b) => {
      const va = a.value ?? 0;
      const vb = b.value ?? 0;
      if (vb !== va) return sort === 'DESC' ? vb - va : va - vb;
      return (a.label ?? '').localeCompare(b.label ?? '');
    });
  }, [data, sort]);

  const isSmallPlot = width < 250;
  const dimensions = isSmallPlot ? DIMENSIONS_SMALL : DIMENSIONS_STANDARD;
  const { fontSize, paddingOuter } = dimensions;

  // Use a unique domain key for each bar (its index) so that duplicate labels
  // are treated as separate entries in the band scale.
  const yDomain = displayData.map((_, i) => i.toString());

  const effectivePaddingInner = barGap / (maxBarThickness + barGap);
  const effectivePaddingOuter = (() => {
    const barCount = yDomain.length;
    if (barCount === 0 || height <= 0) return paddingOuter;
    const minOuter =
      (height * (1 - effectivePaddingInner)) / maxBarThickness -
      barCount +
      effectivePaddingInner;
    return Math.max(0, minOuter / 2);
  })();
  const yScale = scaleBand<string>(yDomain, [0, height])
    .paddingOuter(effectivePaddingOuter)
    .paddingInner(effectivePaddingInner);

  // Prepare display labels that remain unique by appending an index when the
  // same label appears multiple times (e.g., "London", "London (2)").
  const labelCounts = new Map<string, number>();
  const displayLabels = displayData.map((d) => {
    const count = labelCounts.get(d.label) ?? 0;
    labelCounts.set(d.label, count + 1);
    return count === 0 ? d.label : `${d.label} (${count + 1})`;
  });

  const yAxisWidth = Math.min(
    getMaxTextWidth({ texts: displayLabels }),
    width * 0.35
  );

  const allValues = displayData.map((d) => d.value).filter((v) => v !== null);
  const formatValue = getFormatter(format, {
    values: allValues,
    currencyCode,
  });
  const totalBarLabelWidth = getMaxTextWidth({
    texts: allValues.map((v) => formatValue(v)),
  });
  const availableChartWidth = width - yAxisWidth - BAR_LEFT_PADDING;
  const maxBarLabelSize =
    availableChartWidth * MAX_BAR_LABEL_WIDTH_RATIO - BAR_LABEL_PADDING;
  const barLabelSize = Math.min(
    Math.max(MIN_BAR_LABEL_SIZE, totalBarLabelWidth),
    Math.max(0, maxBarLabelSize)
  );
  const plotWidth =
    availableChartWidth -
    (showBarLabels ? barLabelSize + BAR_LABEL_PADDING : 0);

  const isPlotSizeValid = plotWidth > 0 && height > 0;

  const everyValueNegative = allValues.every((v) => v <= 0);
  const everyValuePositive = allValues.every((v) => v >= 0);
  const minValue = xAxis?.min ?? min(allValues) ?? 0;
  const maxValue = xAxis?.max ?? max(allValues) ?? 0;

  const xScale = (() => {
    if (minValue === undefined || maxValue === undefined) return null;

    const forceZero = !xAxis;

    const zeroedMinValue =
      maxValue > 0 && forceZero ? Math.min(minValue, 0) : minValue;
    const zeroedMaxValue = maxValue < 0 && forceZero ? 0 : maxValue;

    const zeroedMaxDomain =
      zeroedMaxValue === zeroedMinValue ? zeroedMinValue + 1 : zeroedMaxValue;

    return scaleLinear([zeroedMinValue, zeroedMaxDomain], [0, plotWidth]);
  })();
  const maxXScaleRange = xScale?.range()[1] ?? plotWidth;

  const tooltipFormatValue = getFormatter(
    format === FormatType.SI ? FormatType.INTEGER : format,
    { values: allValues, increasePrecision: 1, currencyCode }
  );

  const barGeometries = displayData.map(({ value }) => {
    if (value === null) {
      return {
        x: 0,
        barWidth: maxXScaleRange,
        xStart: 0,
        xEnd: maxXScaleRange,
      };
    }
    if (!xScale) return null;
    const baselineValue =
      xAxis && everyValuePositive && minValue ? minValue : 0;
    const scaledStart = xScale(baselineValue);
    const scaledEnd = xScale(value);
    const calculatedWidth = Math.abs(scaledEnd - scaledStart);
    return {
      x: Math.min(scaledStart, scaledEnd),
      barWidth:
        calculatedWidth === 0 ? 0 : Math.max(MIN_BAR_WIDTH, calculatedWidth),
      xStart: scaledStart,
      xEnd: scaledEnd,
    };
  });

  const rowStarts = displayData.map((_, i) => yScale(i.toString()));
  const rowSlop = Math.max(0, (yScale.step() - yScale.bandwidth()) / 2);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const { isVisible, gProps } = useTooltipController({
    onHover: ([px, py], event) => {
      setCursor(getCursorPosition(event));
      const row = findNearestBandIndex(
        rowStarts,
        yScale.bandwidth(),
        py,
        rowSlop
      );
      const geometry = row === null ? null : barGeometries[row];
      const withinBar =
        geometry != null &&
        px >= geometry.x - BAR_END_HOVER_SLOP &&
        px <= geometry.x + geometry.barWidth + BAR_END_HOVER_SLOP;
      setHoveredRow(withinBar ? row : null);
    },
  });
  useEffect(() => {
    if (!isVisible) {
      setHoveredRow(null);
      setCursor(null);
    }
  }, [isVisible]);

  const hoveredDatum =
    hoveredRow === null ? null : (displayData[hoveredRow] ?? null);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.BarChart)}
    >
      <PlotLoadWrapper loading={loading} noData={displayData.length === 0}>
        <svg width="100%" height={height} className={styles.svg}>
          <defs>
            <pattern
              id="diagonal-stripe-4"
              patternUnits="userSpaceOnUse"
              width="10"
              height="10"
            >
              <image
                xlinkHref={noDataPatternHref}
                x="0"
                y="0"
                width="10"
                height="10"
              ></image>
            </pattern>
          </defs>
          <g id="y-axis" data-testid="y-axis">
            {isPlotSizeValid &&
              yDomain.map((domain) => {
                const y = yScale(domain);
                const labelIndex = parseInt(domain, 10);
                const axisLabel = displayLabels[labelIndex] ?? domain;
                return (
                  y !== undefined && (
                    <AxisLabel
                      key={domain}
                      y={y + yScale.bandwidth() / 2}
                      width={yAxisWidth}
                      fontSize={fontSize}
                      label={axisLabel}
                      availableHeight={yScale.bandwidth()}
                    />
                  )
                );
              })}
          </g>
          <g
            id="chart"
            transform={`translate(${yAxisWidth + BAR_LEFT_PADDING}, 0)`}
            {...gProps}
          >
            {isPlotSizeValid && (
              <rect
                className={styles.hoverOverlay}
                x={0}
                y={0}
                width={Math.max(availableChartWidth, 0)}
                height={height}
                fill="transparent"
              />
            )}
            {isPlotSizeValid &&
              xScale &&
              displayData.map((d, i) => {
                const { label, value, color } = d;

                const geometry = barGeometries[i];
                if (geometry == null) return null;
                const { x, barWidth, xStart, xEnd } = geometry;

                const y = yScale(i.toString());
                const maxBarLabelWidth = everyValueNegative
                  ? maxXScaleRange - plotWidth + barLabelSize
                  : Math.max(maxXScaleRange - barWidth + barLabelSize, 0);

                return (
                  barWidth >= 0 &&
                  maxBarLabelWidth >= 0 && (
                    <g key={`${label}_${i}`} id="bar-group">
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={yScale.bandwidth()}
                        rx={0}
                        fill={
                          value === null
                            ? `url(#diagonal-stripe-4)`
                            : color || defaultBarColor
                        }
                        style={
                          {
                            '--replots-dim-color':
                              value === null
                                ? 'url(#diagonal-stripe-4)'
                                : getDimmedPlotColor(
                                    color || defaultBarColor,
                                    theme
                                  ),
                          } as CSSProperties
                        }
                        className={classNames(
                          styles.bar,
                          hoveredRow !== null &&
                            hoveredRow !== i &&
                            styles.barDimmed
                        )}
                        data-testid="bar-chart__bar"
                      />
                      {showBarLabels && y !== undefined && (
                        <BarLabel
                          x={Math.max(xEnd, xStart) + BAR_LABEL_PADDING}
                          y={y}
                          width={maxBarLabelWidth}
                          height={yScale.bandwidth()}
                          fontSize={fontSize}
                          label={value === null ? '' : formatValue(value)}
                        />
                      )}
                    </g>
                  )
                );
              })}
          </g>
        </svg>
        {hoveredDatum !== null && cursor !== null && (
          <PlotTooltip
            isVisible={isVisible}
            x={cursor.x}
            y={cursor.y}
            placement="vertical"
            space="viewport"
          >
            {tooltipContent ? (
              tooltipContent(hoveredDatum)
            ) : (
              <div>
                <div className={styles.barTooltipLabel}>
                  {hoveredDatum.label}
                </div>
                <div className={styles.barTooltipValue}>
                  {hoveredDatum.value === null
                    ? '-'
                    : tooltipFormatValue(hoveredDatum.value)}
                </div>
              </div>
            )}
          </PlotTooltip>
        )}
      </PlotLoadWrapper>
    </div>
  );
};
